import { css, html, LitElement } from 'lit';
import { customElement } from 'lit/decorators.js';
import { getGlobalStyleSheets } from '../../global-styles';
import { AltMessagingGroupChatProps } from './messaging.props';
import { IconEnums } from '../icon';
import { AtlantisEvent } from '../helpers';
import { ifDefined } from 'lit/directives/if-defined.js';

import * as TagNames from './messaging.tagnames';
import { ButtonEnums } from '../button';
import { classMap } from 'lit/directives/class-map.js';

@customElement(TagNames.MessagingGroupChat)
export class AtlMessagingGroupChat
   extends LitElement
   implements AltMessagingGroupChatProps
{
   declare pictureUrl?: string;
   declare name?: string;
   declare text?: string;
   declare date?: string;
   declare userRole?: string;
   declare email?: string;
   declare ariaTitle?: string;
   declare actionMenuAriaLabel?: string;
   declare bodyClassName?: string;
   declare active?: boolean;
   declare clickable?: boolean;
   declare _hasOptions?: boolean;

   static properties = {
      pictureUrl: { attribute: 'picture-url', type: String },
      name: { type: String },
      text: { type: String },
      date: { type: String },
      userRole: { type: String },
      email: { type: String },
      ariaTitle: { type: String },
      actionMenuAriaLabel: { type: String },
      bodyClassName: { type: String },
      active: { type: Boolean },
      clickable: { type: Boolean },
      _hasOptions: { type: Boolean, state: true },
   };

   static styles = [
      ...getGlobalStyleSheets(),
      css`
         .group-chat-icon {
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 2px solid #6d6d6d;
            color: #6d6d6d;
            border-radius: 50%;
         }

         .messaging-group-chat {
            align-items: stretch;
         }

         atlantis-dropdown {
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
         }

         atlantis-dropdown::part(dropdown),
         atlantis-dropdown::part(button) {
            height: 100%;
         }

         atlantis-dropdown::part(button) {
            font-size: 24px;
            color: #a6a6a6;
            transition: color 0.3s ease;
            min-width: 2.5rem;
         }

         atlantis-dropdown::part(button):hover {
            color: #3e3e3e;
         }
      `,
   ];

   renderMessageAvatar() {
      if (this.pictureUrl) {
         return html`<img src=${this.pictureUrl} alt="avatar" />`;
      }

      return html`
         <atlantis-icon
            source=${IconEnums.IconSource.FontAwesome}
            name="user"
            size=${IconEnums.IconSizes.Medium}
            class="group-chat-icon"
         >
         </atlantis-icon>
      `;
   }

   handleSlotchange(e: AtlantisEvent<Event, HTMLSlotElement>) {
      const childNodes = e.target.assignedNodes({ flatten: true });
      this._hasOptions = childNodes.length > 0;
   }

   private _renderMessagingChatActionMenu = () => {
      return html`
         <atlantis-dropdown
            toggle-icon-name="ellipsis-vertical"
            toggle-classes="action-item"
            toggle-aria-label=${ifDefined(this.actionMenuAriaLabel)}
            variant=${ButtonEnums.ButtonVariant.Action}
         >
            <slot name="actionMenu"></slot>
         </atlantis-dropdown>
      `;
   };

   private _renderChatTitle = () => {
      const contents = [];
      if (this.name) {
         contents.push(
            html`<span class="messaging-group-chat-name">${this.name}</span>`
         );
      }
      if (this.date) {
         contents.push(
            html`<span class="messaging-group-chat-date">${this.date}</span>`
         );
      }
      return html`<div class="messaging-group-chat-title">${contents}</div>`;
   };

   private _renderChatBodyContent = () => {
      const contents = [this._renderChatTitle()];
      if (this.userRole) {
         contents.push(
            html`<small class="group-chat-card-role">${this.userRole}</small>`
         );
      }
      if (this.email) {
         contents.push(
            html`<a class="group-chat-card-email" href="mailto:${this.email}"
               >${this.email}</a
            >`
         );
      }
      if (this.text) {
         contents.push(
            html`<p class="messaging-group-chat-text">${this.text}</p>`
         );
      }
      return html`${contents}`;
   };

   render() {
      const classNames = {
         'messaging-group-chat': true,
         active: this.active === true,
         clickable: this.clickable === true,
      };

      return html`
         <div class=${classMap(classNames)} title=${ifDefined(this.ariaTitle)}>
            <div class="messaging-group-chat-content">
               <div class="messaging-group-chat-avatar">
                  ${this.renderMessageAvatar()}
               </div>
               <div class="messaging-group-chat-body ${this.bodyClassName}">
                  ${this._renderChatBodyContent()}
               </div>
            </div>
            <div class="messaging-group-chat-action">
               ${this._renderMessagingChatActionMenu()}
            </div>
         </div>
      `;
   }
}
